import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { GridColDef } from '@mui/x-data-grid';
import { useCurrentUser } from '../hooks/useCurrentUser';
import apiClient, { usersApi, DbUser } from '../api/client';
import { canEditPage } from '../utils/rbac';
import { titleCase } from '../utils/format';
import Grid from '@mui/material/Grid';
import { PageHeader } from './layout/PageHeader';
import { PageLoader } from './PageLoader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { ChartCard } from './charts/ChartCard';
import { NexusBarChart, NexusDonut } from './charts/NexusCharts';
import { tokens } from '../theme/tokens';
import { portfoliosApi, ppmProjectsApi, PortfolioListItem, PpmProjectListItem } from '../api/ppm';

type RaidTab = 'risks' | 'issues' | 'assumptions' | 'dependencies';

const RESOURCE: Record<RaidTab, { label: string; basePath: string; titleField: string }> = {
  risks: { label: 'Risks', basePath: '/risks', titleField: 'title' },
  issues: { label: 'Issues', basePath: '/issues', titleField: 'title' },
  assumptions: { label: 'Assumptions', basePath: '/assumptions', titleField: 'title' },
  dependencies: { label: 'Dependencies', basePath: '/dependencies', titleField: 'description' },
};

const pretty = (v: any) => JSON.stringify(v ?? {}, null, 2);

const Raid: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'raid');
  const [me, setMe] = useState<DbUser | null>(null);

  useEffect(() => {
    usersApi.me().then((r) => setMe((r.data || null) as any)).catch(() => setMe(null));
  }, []);

  const [tab, setTab] = useState<RaidTab>('risks');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [jsonText, setJsonText] = useState('{}');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState<any>({});

  const [projectFilterPortfolioId, setProjectFilterPortfolioId] = useState<string>('');
  const [portfolios, setPortfolios] = useState<PortfolioListItem[]>([]);
  const [projects, setProjects] = useState<PpmProjectListItem[]>([]);

  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string }>({ severity: 'success', message: '' });

  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsRow, setActionsRow] = useState<any | null>(null);
  const actionsOpen = Boolean(actionsAnchorEl);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [topPanel, setTopPanel] = useState<null | 'total' | 'open' | 'needsApproval' | 'overdue'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get(RESOURCE[tab].basePath);
      setRows(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load RAID data');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  const refreshRefs = useCallback(async () => {
    try {
      if (canEdit) {
        const [pRes, internalRes, jiraRes] = await Promise.all([
          portfoliosApi.getAll(),
          ppmProjectsApi.getAll({ source: 'internal' }),
          ppmProjectsApi.getAll({ source: 'jira' }),
        ]);
        setPortfolios((pRes.data || []) as PortfolioListItem[]);
        setProjects([...(internalRes.data || []), ...(jiraRes.data || [])]);
      } else {
        // Non-admin: no portfolios, and projects list is already scoped to "mine"
        const internalRes = await ppmProjectsApi.getAll({ source: 'internal' });
        setPortfolios([]);
        setProjects([...(internalRes.data || [])]);
      }
    } catch {
      // non-fatal; user can still paste IDs manually
      setPortfolios([]);
      setProjects([]);
    }
  }, [canEdit]);

  const summary = useMemo(() => {
    const total = rows.length;
    const open = rows.filter((r) => !String(r.status || '').toUpperCase().includes('CLOSED')).length;
    const needsApproval = rows.filter((r) => String(r.status || '').toUpperCase().includes('APPROVAL')).length;
    const overdue = rows.filter((r) => r.dueDate && new Date(r.dueDate) < new Date()).length;
    return { total, open, needsApproval, overdue };
  }, [rows]);

  const statusMix = useMemo(() => {
    const map = new Map<string, number>();
    (rows || []).forEach((r: any) => {
      const s = String(r.status || 'UNSPECIFIED').toUpperCase();
      map.set(s, (map.get(s) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [rows]);

  const dueBuckets = useMemo(() => {
    const buckets = [
      { name: 'Overdue', value: 0 },
      { name: 'Due <7d', value: 0 },
      { name: 'Due 7–30d', value: 0 },
      { name: 'Due 30d+', value: 0 },
      { name: 'No due', value: 0 },
    ];
    const now = Date.now();
    (rows || []).forEach((r: any) => {
      if (!r.dueDate) {
        buckets[4].value += 1;
        return;
      }
      const due = new Date(r.dueDate).getTime();
      if (!Number.isFinite(due)) {
        buckets[4].value += 1;
        return;
      }
      const days = (due - now) / (1000 * 60 * 60 * 24);
      if (days < 0) buckets[0].value += 1;
      else if (days < 7) buckets[1].value += 1;
      else if (days < 30) buckets[2].value += 1;
      else buckets[3].value += 1;
    });
    return buckets;
  }, [rows]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    refreshRefs();
  }, [refreshRefs]);

  const portfolioById = useMemo(() => {
    const m = new Map<string, PortfolioListItem>();
    (portfolios || []).forEach((p) => m.set(String(p.id), p));
    return m;
  }, [portfolios]);

  const projectById = useMemo(() => {
    const m = new Map<string, PpmProjectListItem>();
    (projects || []).forEach((p) => m.set(String(p.id), p));
    return m;
  }, [projects]);

  const projectsForPicker = useMemo(() => {
    const filtered = projectFilterPortfolioId
      ? (projects || []).filter((p) => String(p.portfolioId || '') === String(projectFilterPortfolioId))
      : (projects || []);
    return filtered.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [projects, projectFilterPortfolioId]);

  // Deep-link support: /raid?tab=risks&openId=<id>
  useEffect(() => {
    const tabParam = searchParams.get('tab') as RaidTab | null;
    const openId = searchParams.get('openId');
    if (tabParam && tabParam !== tab) setTab(tabParam);
    if (!openId) return;
    const found = rows.find((r) => String(r.id) === String(openId));
    if (!found) return;
    setActionsRow(found);
    setDetailsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, searchParams]);

  const cols: GridColDef[] = useMemo(() => {
    const titleField = RESOURCE[tab].titleField;
    const base: GridColDef[] = [
      { field: 'id', headerName: 'ID', width: 220 },
      {
        field: titleField,
        headerName: RESOURCE[tab].label.slice(0, -1),
        flex: 1,
        minWidth: 260,
        valueGetter: (p) => p.row?.[titleField] || '—',
      },
      { field: 'status', headerName: 'Status', width: 160, valueGetter: (p) => (p.row?.status ? titleCase(String(p.row.status)) : '—') },
      {
        field: 'projectId',
        headerName: 'Project',
        width: 240,
        valueGetter: (p) => {
          const id = p.row?.projectId ? String(p.row.projectId) : '';
          if (!id) return '—';
          const proj = projectById.get(id);
          return proj?.name ? proj.name : id;
        },
      },
      {
        field: 'portfolioId',
        headerName: 'Portfolio',
        width: 240,
        valueGetter: (p) => {
          const explicit = p.row?.portfolioId ? String(p.row.portfolioId) : '';
          const fromProject = p.row?.projectId ? String(projectById.get(String(p.row.projectId))?.portfolioId || '') : '';
          const id = explicit || fromProject;
          if (!id) return '—';
          const portfolio = portfolioById.get(id);
          return portfolio?.name ? portfolio.name : id;
        },
      },
      { field: 'createdAt', headerName: 'Created', width: 180, valueGetter: (p) => (p.row?.createdAt ? String(p.row.createdAt).slice(0, 19).replace('T', ' ') : '—') },
    ];

    if (!canEdit) return base;

    return [
      ...base,
      {
        field: 'actions',
        headerName: 'Admin',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setActionsAnchorEl(e.currentTarget);
              setActionsRow(params.row);
            }}
            aria-label="Actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
    ];
  }, [canEdit, tab, portfolioById, projectById]);

  const openCreate = () => {
    setEditing(null);
    setShowAdvanced(false);
    setProjectFilterPortfolioId('');
    const firstProjectId = projectsForPicker[0]?.id ? String(projectsForPicker[0].id) : '';
    const secondProjectId = projectsForPicker[1]?.id ? String(projectsForPicker[1].id) : firstProjectId;

    if (tab === 'risks') {
      setForm({
        projectId: firstProjectId || 'demo_project_ehr',
        title: '',
        description: '',
        status: 'NEW',
        probability: 'MEDIUM',
        impact: 'MEDIUM',
        mitigation: '',
        contingency: '',
        ownerId: me?.id || '',
        creatorId: me?.id || '',
        dueDate: '',
      });
    } else if (tab === 'issues') {
      setForm({
        projectId: firstProjectId || 'demo_project_ehr',
        title: '',
        description: '',
        status: 'NEW',
        priority: 'MEDIUM',
        ownerId: me?.id || '',
        creatorId: me?.id || '',
        dueDate: '',
        resolution: '',
      });
    } else if (tab === 'assumptions') {
      setForm({
        // Non-admin assumptions require a projectId (backend enforces access by project membership)
        projectId: firstProjectId || '',
        portfolioId: '',
        title: '',
        description: '',
        validated: false,
        validationNotes: '',
      });
    } else {
      setForm({
        dependentProjectId: firstProjectId || 'demo_project_ehr',
        dependencyProjectId: secondProjectId || 'demo_project_portal',
        type: 'FINISH_TO_START',
        status: 'ACTIVE',
        description: '',
        lagDays: '',
      });
    }

    setJsonText(pretty({}));
    setEditOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setShowAdvanced(false);
    setJsonText(pretty(row));

    if (tab === 'risks') {
      setForm({
        projectId: row.projectId || 'demo_project_ehr',
        title: row.title || '',
        description: row.description || '',
        status: row.status || 'NEW',
        probability: row.probability || 'MEDIUM',
        impact: row.impact || 'MEDIUM',
        mitigation: row.mitigation || '',
        contingency: row.contingency || '',
        ownerId: row.ownerId || '',
        creatorId: row.creatorId || 'demo_user_pm',
        dueDate: row.dueDate ? String(row.dueDate).slice(0, 10) : '',
      });
    } else if (tab === 'issues') {
      setForm({
        projectId: row.projectId || 'demo_project_ehr',
        title: row.title || '',
        description: row.description || '',
        status: row.status || 'NEW',
        priority: row.priority || 'MEDIUM',
        ownerId: row.ownerId || '',
        creatorId: row.creatorId || 'demo_user_pm',
        dueDate: row.dueDate ? String(row.dueDate).slice(0, 10) : '',
        resolution: row.resolution || '',
      });
    } else if (tab === 'assumptions') {
      setForm({
        projectId: row.projectId || '',
        portfolioId: row.portfolioId || '',
        title: row.title || '',
        description: row.description || '',
        validated: !!row.validated,
        validationNotes: row.validationNotes || '',
      });
    } else {
      setForm({
        dependentProjectId: row.dependentProjectId || '',
        dependencyProjectId: row.dependencyProjectId || '',
        type: row.type || 'FINISH_TO_START',
        status: row.status || 'ACTIVE',
        description: row.description || '',
        lagDays: row.lagDays != null ? String(row.lagDays) : '',
      });
    }

    if (tab === 'risks' || tab === 'issues') {
      const pid = row?.projectId ? String(row.projectId) : '';
      const portfolioId = pid ? String(projectById.get(pid)?.portfolioId || '') : '';
      setProjectFilterPortfolioId(portfolioId);
    }

    setEditOpen(true);
  };

  const save = async () => {
    try {
      let payload: any = {};

      if (tab === 'risks') {
        if (!form.projectId?.trim() || !form.title?.trim() || !form.description?.trim()) {
          throw new Error('Project ID, Title, and Description are required');
        }
        payload = {
          projectId: form.projectId.trim(),
          title: form.title.trim(),
          description: form.description.trim(),
          status: form.status,
          probability: form.probability,
          impact: form.impact,
          mitigation: form.mitigation?.trim() ? form.mitigation.trim() : undefined,
          contingency: form.contingency?.trim() ? form.contingency.trim() : undefined,
          dueDate: form.dueDate?.trim() ? new Date(`${form.dueDate}T00:00:00.000Z`) : undefined,
        };
      } else if (tab === 'issues') {
        if (!form.projectId?.trim() || !form.title?.trim() || !form.description?.trim()) {
          throw new Error('Project ID, Title, and Description are required');
        }
        payload = {
          projectId: form.projectId.trim(),
          title: form.title.trim(),
          description: form.description.trim(),
          status: form.status,
          priority: form.priority,
          dueDate: form.dueDate?.trim() ? new Date(`${form.dueDate}T00:00:00.000Z`) : undefined,
          resolution: form.resolution?.trim() ? form.resolution.trim() : undefined,
        };
      } else if (tab === 'assumptions') {
        if (!form.title?.trim() || !form.description?.trim()) {
          throw new Error('Title and Description are required');
        }
        if (!canEdit && !form.projectId?.trim()) {
          throw new Error('Project is required for assumptions');
        }
        payload = {
          projectId: form.projectId?.trim() ? form.projectId.trim() : undefined,
          portfolioId: form.portfolioId?.trim() ? form.portfolioId.trim() : undefined,
          title: form.title.trim(),
          description: form.description.trim(),
          validated: !!form.validated,
          validationNotes: form.validationNotes?.trim() ? form.validationNotes.trim() : undefined,
        };
      } else {
        if (!form.dependentProjectId?.trim() || !form.dependencyProjectId?.trim()) {
          throw new Error('Dependent Project ID and Dependency Project ID are required');
        }
        payload = {
          dependentProjectId: form.dependentProjectId.trim(),
          dependencyProjectId: form.dependencyProjectId.trim(),
          type: form.type,
          status: form.status,
          description: form.description?.trim() ? form.description.trim() : undefined,
          lagDays: form.lagDays?.trim() ? Number(form.lagDays) : undefined,
        };
      }

      if (showAdvanced) {
        try {
          const advanced = JSON.parse(jsonText || '{}');
          Object.assign(payload, advanced);
        } catch {
          // ignore invalid JSON
        }
      }
      if (editing?.id) {
        await apiClient.put(`${RESOURCE[tab].basePath}/${editing.id}`, payload);
        setToast({ severity: 'success', message: 'Updated' });
      } else {
        await apiClient.post(RESOURCE[tab].basePath, payload);
        setToast({ severity: 'success', message: 'Created' });
      }
      setToastOpen(true);
      setEditOpen(false);
      refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.message || 'Save failed (invalid JSON?)' });
      setToastOpen(true);
    }
  };

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="RAID"
        subtitle={canEdit ? "Operational tracking across risks, assumptions, issues, and dependencies." : "Your RAID items (scoped to you)."}
        actions={
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              {Object.entries(RESOURCE)
                .map(([k, v]) => (
                  <Tab key={k} value={k} label={v.label} />
                ))}
            </Tabs>
            <Button variant="outlined" onClick={refresh} sx={{ borderRadius: 2 }}>
              Refresh
            </Button>
            <Button variant="contained" onClick={openCreate} sx={{ borderRadius: 2 }}>
              New
            </Button>
          </>
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title={`Total (${RESOURCE[tab].label})`}
            value={summary.total}
            subtitle="Loaded"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'total' ? null : 'total'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Open"
            value={summary.open}
            subtitle="Not closed"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'open' ? null : 'open'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Needs approval"
            value={summary.needsApproval}
            subtitle="Workflow"
            accent={tokens.color.warning}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'needsApproval' ? null : 'needsApproval'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Overdue"
            value={summary.overdue}
            subtitle="Past due date"
            accent={tokens.color.danger}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'overdue' ? null : 'overdue'));
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={!!topPanel} unmountOnExit>
            <TableCard
              title={topPanel === 'total' ? `${RESOURCE[tab].label}` : topPanel === 'open' ? `Open ${RESOURCE[tab].label.toLowerCase()}` : topPanel === 'needsApproval' ? `Needs approval` : 'Overdue'}
              actions={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)} sx={{ borderRadius: 2 }}>
                    {showAllPanel ? 'Show less' : 'Show all'}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setTopPanel(null)} sx={{ borderRadius: 2 }}>
                    Close
                  </Button>
                </Stack>
              }
            >
              {(() => {
                const list =
                  topPanel === 'total'
                    ? rows
                    : topPanel === 'open'
                      ? rows.filter((r) => !String(r.status || '').toUpperCase().includes('CLOSED'))
                      : topPanel === 'needsApproval'
                        ? rows.filter((r) => String(r.status || '').toUpperCase().includes('APPROVAL'))
                        : rows.filter((r) => r.dueDate && new Date(r.dueDate) < new Date());

                if (!list.length) {
                  return (
                    <Alert severity="info" variant="outlined">
                      No results.
                    </Alert>
                  );
                }

                const titleField = RESOURCE[tab].titleField;
                const sorted = list
                  .slice()
                  .sort((a, b) => String(a.dueDate || '').localeCompare(String(b.dueDate || '')));

                return (
                  <Stack spacing={1}>
                    {sorted.slice(0, showAllPanel ? 1000 : 5).map((r: any) => (
                      <Box
                        key={r.id}
                        sx={{ p: 1, borderRadius: `${tokens.radius.sm}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                        onClick={() => {
                          setActionsRow(r);
                          setDetailsOpen(true);
                        }}
                      >
                        <Typography sx={{ fontWeight: 650 }} noWrap title={String(r[titleField] || '')}>
                          {String(r[titleField] || 'Untitled')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {r.status ? titleCase(String(r.status)) : '—'}
                          {r.dueDate ? ` • due ${String(r.dueDate).slice(0, 10)}` : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                );
              })()}
            </TableCard>
          </Collapse>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <ChartCard title="Status mix" subtitle={`${RESOURCE[tab].label} by status`} height={260}>
            <NexusDonut
              data={statusMix.map((s) => ({ name: titleCase(s.name), value: s.value }))}
              height={200}
              centerLabel="Total"
              centerValue={String(summary.total)}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Due date buckets" subtitle="Overdue vs upcoming" height={260}>
            <NexusBarChart
              data={dueBuckets}
              xKey="name"
              series={[{ key: 'value', name: 'Items', color: tokens.color.accent }]}
              height={220}
              showLabels
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Workflow snapshot" subtitle="Open + approval + overdue" height={260}>
            <NexusBarChart
              data={[
                { name: 'Open', value: summary.open },
                { name: 'Needs approval', value: summary.needsApproval },
                { name: 'Overdue', value: summary.overdue },
              ]}
              xKey="name"
              series={[{ key: 'value', name: 'Count', color: tokens.color.accent }]}
              height={220}
              showLabels
            />
          </ChartCard>
        </Grid>
      </Grid>

      <TableCard
        title={`${RESOURCE[tab].label} list`}
        subtitle="Click a row for details. Admin actions are in the overflow menu."
        actions={loading ? <CircularProgress size={18} /> : null}
      >
        <div style={{ height: 600, width: '100%' }}>
          <ResizableDataGrid
            storageKey={`gridWidths.raid.${tab}`}
            enableDensityControls
            rows={rows}
            columns={cols}
            getRowId={(r) => r.id}
            disableRowSelectionOnClick
            onRowClick={(p) => {
              setActionsRow(p.row);
              setDetailsOpen(true);
            }}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': { backgroundColor: tokens.color.surfaceMuted },
              '& .MuiDataGrid-row:hover': { backgroundColor: tokens.color.surfaceMuted },
            }}
          />
        </div>
      </TableCard>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          {editing ? `Edit ${RESOURCE[tab].label.slice(0, -1)}` : `New ${RESOURCE[tab].label.slice(0, -1)}`}
        </DialogTitle>
        <DialogContent>
          {canEdit ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              Demo IDs: project `demo_project_ehr`, portfolio `demo_portfolio_core`, user `demo_user_pm`.
            </Alert>
          ) : null}

          {tab === 'risks' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25, mt: 1 }}>
              <TextField
                label="Portfolio (for project picker)"
                value={projectFilterPortfolioId}
                onChange={(e) => {
                  const next = e.target.value;
                  setProjectFilterPortfolioId(next);
                  setForm((f: any) => ({ ...f, projectId: '' }));
                }}
                select
                fullWidth
              >
                <MenuItem value="">
                  <em>All portfolios</em>
                </MenuItem>
                {portfolios.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Project"
                value={form.projectId || ''}
                onChange={(e) => setForm((f: any) => ({ ...f, projectId: e.target.value }))}
                select
                fullWidth
                helperText={projects.length ? undefined : 'Loading project list… (or use Advanced JSON to paste an ID)'}
              >
                <MenuItem value="">
                  <em>Select project…</em>
                </MenuItem>
                {projectsForPicker.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              {canEdit ? (
                <Alert severity="info" variant="outlined">
                  Owner/Creator are set automatically to <b>{me?.username || me?.email || 'you'}</b>.
                </Alert>
              ) : null}
              <TextField label="Title" value={form.title || ''} onChange={(e) => setForm((f: any) => ({ ...f, title: e.target.value }))} fullWidth sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }} />
              <TextField label="Description" value={form.description || ''} onChange={(e) => setForm((f: any) => ({ ...f, description: e.target.value }))} fullWidth multiline minRows={3} sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }} />
              <TextField label="Status" value={form.status || 'NEW'} onChange={(e) => setForm((f: any) => ({ ...f, status: e.target.value }))} select fullWidth>
                {['NEW', 'IN_WORK', 'SUBMITTED_FOR_APPROVAL', 'APPROVED', 'CLOSED', 'UNDEFINED'].map((s) => (
                  <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>
                ))}
              </TextField>
              <TextField label="Probability" value={form.probability || 'MEDIUM'} onChange={(e) => setForm((f: any) => ({ ...f, probability: e.target.value }))} select fullWidth>
                {['VERY_LOW', 'LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH'].map((s) => (
                  <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>
                ))}
              </TextField>
              <TextField label="Impact" value={form.impact || 'MEDIUM'} onChange={(e) => setForm((f: any) => ({ ...f, impact: e.target.value }))} select fullWidth>
                {['VERY_LOW', 'LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH', 'CRITICAL'].map((s) => (
                  <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>
                ))}
              </TextField>
              <TextField label="Due Date (optional)" type="date" value={form.dueDate || ''} onChange={(e) => setForm((f: any) => ({ ...f, dueDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Mitigation (optional)" value={form.mitigation || ''} onChange={(e) => setForm((f: any) => ({ ...f, mitigation: e.target.value }))} fullWidth multiline minRows={2} sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }} />
              <TextField label="Contingency (optional)" value={form.contingency || ''} onChange={(e) => setForm((f: any) => ({ ...f, contingency: e.target.value }))} fullWidth multiline minRows={2} sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }} />
              {/* Creator is set automatically */}
            </Box>
          ) : null}

          {tab === 'issues' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25, mt: 1 }}>
              <TextField
                label="Portfolio (for project picker)"
                value={projectFilterPortfolioId}
                onChange={(e) => {
                  const next = e.target.value;
                  setProjectFilterPortfolioId(next);
                  setForm((f: any) => ({ ...f, projectId: '' }));
                }}
                select
                fullWidth
              >
                <MenuItem value="">
                  <em>All portfolios</em>
                </MenuItem>
                {portfolios.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Project"
                value={form.projectId || ''}
                onChange={(e) => setForm((f: any) => ({ ...f, projectId: e.target.value }))}
                select
                fullWidth
                helperText={projects.length ? undefined : 'Loading project list… (or use Advanced JSON to paste an ID)'}
              >
                <MenuItem value="">
                  <em>Select project…</em>
                </MenuItem>
                {projectsForPicker.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              {canEdit ? (
                <Alert severity="info" variant="outlined">
                  Owner/Creator are set automatically to <b>{me?.username || me?.email || 'you'}</b>.
                </Alert>
              ) : null}
              <TextField label="Title" value={form.title || ''} onChange={(e) => setForm((f: any) => ({ ...f, title: e.target.value }))} fullWidth sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }} />
              <TextField label="Description" value={form.description || ''} onChange={(e) => setForm((f: any) => ({ ...f, description: e.target.value }))} fullWidth multiline minRows={3} sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }} />
              <TextField label="Status" value={form.status || 'NEW'} onChange={(e) => setForm((f: any) => ({ ...f, status: e.target.value }))} select fullWidth>
                {['NEW', 'IN_WORK', 'SUBMITTED_FOR_APPROVAL', 'APPROVED', 'CLOSED', 'UNDEFINED'].map((s) => (
                  <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>
                ))}
              </TextField>
              <TextField label="Priority" value={form.priority || 'MEDIUM'} onChange={(e) => setForm((f: any) => ({ ...f, priority: e.target.value }))} select fullWidth>
                {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((s) => (
                  <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>
                ))}
              </TextField>
              <TextField label="Due Date (optional)" type="date" value={form.dueDate || ''} onChange={(e) => setForm((f: any) => ({ ...f, dueDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Resolution (optional)" value={form.resolution || ''} onChange={(e) => setForm((f: any) => ({ ...f, resolution: e.target.value }))} fullWidth />
              {/* Creator is set automatically */}
            </Box>
          ) : null}

          {tab === 'assumptions' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25, mt: 1 }}>
              <TextField
                label="Portfolio (optional)"
                value={form.portfolioId || ''}
                onChange={(e) => setForm((f: any) => ({ ...f, portfolioId: e.target.value }))}
                select
                fullWidth
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {portfolios.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Project (optional)"
                value={form.projectId || ''}
                onChange={(e) => setForm((f: any) => ({ ...f, projectId: e.target.value }))}
                select
                fullWidth
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {(projects || [])
                  .slice()
                  .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
              </TextField>
              <TextField label="Title" value={form.title || ''} onChange={(e) => setForm((f: any) => ({ ...f, title: e.target.value }))} fullWidth sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }} />
              <TextField label="Description" value={form.description || ''} onChange={(e) => setForm((f: any) => ({ ...f, description: e.target.value }))} fullWidth multiline minRows={3} sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }} />
              <FormControlLabel control={<Switch checked={!!form.validated} onChange={(e) => setForm((f: any) => ({ ...f, validated: e.target.checked }))} />} label="Validated" />
              <Box />
              <TextField label="Validation Notes (optional)" value={form.validationNotes || ''} onChange={(e) => setForm((f: any) => ({ ...f, validationNotes: e.target.value }))} fullWidth multiline minRows={2} sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }} />
            </Box>
          ) : null}

          {tab === 'dependencies' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25, mt: 1 }}>
              <TextField
                label="Dependent Project"
                value={form.dependentProjectId || ''}
                onChange={(e) => setForm((f: any) => ({ ...f, dependentProjectId: e.target.value }))}
                select
                fullWidth
              >
                <MenuItem value="">
                  <em>Select project…</em>
                </MenuItem>
                {(projects || [])
                  .slice()
                  .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
              </TextField>
              <TextField
                label="Dependency Project"
                value={form.dependencyProjectId || ''}
                onChange={(e) => setForm((f: any) => ({ ...f, dependencyProjectId: e.target.value }))}
                select
                fullWidth
              >
                <MenuItem value="">
                  <em>Select project…</em>
                </MenuItem>
                {(projects || [])
                  .slice()
                  .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
              </TextField>
              <TextField label="Type" value={form.type || 'FINISH_TO_START'} onChange={(e) => setForm((f: any) => ({ ...f, type: e.target.value }))} select fullWidth>
                {['FINISH_TO_START', 'START_TO_START', 'FINISH_TO_FINISH', 'START_TO_FINISH', 'BLOCKING', 'RELATED'].map((s) => (
                  <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>
                ))}
              </TextField>
              <TextField label="Status" value={form.status || 'ACTIVE'} onChange={(e) => setForm((f: any) => ({ ...f, status: e.target.value }))} select fullWidth>
                {['ACTIVE', 'RESOLVED', 'CANCELLED'].map((s) => (
                  <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>
                ))}
              </TextField>
              <TextField label="Lag Days (optional)" value={form.lagDays || ''} onChange={(e) => setForm((f: any) => ({ ...f, lagDays: e.target.value }))} fullWidth />
              <TextField label="Description (optional)" value={form.description || ''} onChange={(e) => setForm((f: any) => ({ ...f, description: e.target.value }))} fullWidth multiline minRows={3} sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }} />
            </Box>
          ) : null}

          <Accordion sx={{ mt: 1.25 }} expanded={showAdvanced} onChange={(_, v) => setShowAdvanced(v)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 650 }}>Advanced (JSON override)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                fullWidth
                multiline
                minRows={10}
                sx={{
                  mt: 0.5,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  '& .MuiInputBase-input': { fontSize: 12 },
                }}
              />
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toastOpen} autoHideDuration={2500} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={actionsAnchorEl}
        open={actionsOpen}
        onClose={() => {
          setActionsAnchorEl(null);
          setActionsRow(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (actionsRow) openEdit(actionsRow);
            setActionsAnchorEl(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (!actionsRow) return;
            // eslint-disable-next-line no-alert
            const ok = window.confirm(`Delete ${RESOURCE[tab].label.slice(0, -1)}?`);
            if (!ok) return;
            try {
              await apiClient.delete(`${RESOURCE[tab].basePath}/${actionsRow.id}`);
              setToast({ severity: 'success', message: 'Deleted' });
              setToastOpen(true);
              refresh();
            } catch (e: any) {
              setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Delete failed' });
              setToastOpen(true);
            } finally {
              setActionsAnchorEl(null);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{RESOURCE[tab].label.slice(0, -1)}</DialogTitle>
        <DialogContent>
          {actionsRow ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={actionsRow.status ? titleCase(String(actionsRow.status)) : '—'} variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
                {actionsRow.projectId ? <Chip size="small" label={`Project ${actionsRow.projectId}`} variant="outlined" sx={{ height: 22, fontWeight: 700 }} /> : null}
                {actionsRow.portfolioId ? <Chip size="small" label={`Portfolio ${actionsRow.portfolioId}`} variant="outlined" sx={{ height: 22, fontWeight: 700 }} /> : null}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 650 }}>
                {actionsRow[RESOURCE[tab].titleField] || actionsRow.title || actionsRow.description || '—'}
              </Typography>
              {actionsRow.description && RESOURCE[tab].titleField !== 'description' ? (
                <Typography variant="body2">{actionsRow.description}</Typography>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {actionsRow ? (
            <>
              <Button variant="outlined" onClick={() => openEdit(actionsRow)}>
                Edit
              </Button>
              <Button color="error" variant="outlined" onClick={async () => {
                // eslint-disable-next-line no-alert
                const ok = window.confirm(`Delete ${RESOURCE[tab].label.slice(0, -1)}?`);
                if (!ok) return;
                try {
                  await apiClient.delete(`${RESOURCE[tab].basePath}/${actionsRow.id}`);
                  setToast({ severity: 'success', message: 'Deleted' });
                  setToastOpen(true);
                  setDetailsOpen(false);
                  refresh();
                } catch (e: any) {
                  setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Delete failed' });
                  setToastOpen(true);
                }
              }}>
                Delete
              </Button>
            </>
          ) : null}
          <Button variant="contained" onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </PageLoader>
  );
};

export default Raid;


